import SysColumn from "app/class/sys-column";

export type FlussValue = string | number | boolean | null;

export type ScopeKind = "record" | "variable" | "literal";

export interface ScopeOption {
    name: string;
    label: string;
    type: string;
    required?: boolean;
    referencedTableName?: string;
    /**
     * Real sys_columns.id, when this option represents an actual record column (as opposed to a
     * Fluss process variable, which has no backing column). PRIM-010: the Fluss condition builder
     * needs this to resolve CHOICE options (matched by ref_column) and REFERENCE lookups.
     */
    id?: string;
    /**
     * PRIM-022 (round 6): the real backing sys_column, when this option represents an actual
     * record column. Lets the DataPicker's "Enter fixed value" delegate to the platform's own
     * ValueField/FIELD_COMPONENT_MAP (honoring reference_filter, CHOICE options, currency
     * formatting, etc.) instead of a generic text box that only ever looked at `type`.
     */
    column?: SysColumn;
}

export interface FieldMapping {
    target: string;
    source: FlussValue | undefined;
}

export interface DurationValue {
    amount: number | null;
    unit: "minutes" | "hours" | "business_hours" | "days";
}

export interface EventTypeOption {
    id: string;
    name: string;
    label: string;
    active: boolean;
}

export interface QueueOption {
    roleName: string;
    groupName: string;
    label?: string;
}

// Onda 11 D10 supersedes PRIM-023: all active roles can receive tasks, because the inbox
// already matches every effective role. A role without a group remains selectable.
export interface QueueRoleOption {
    roleName: string;
    label: string;
}

// One entry per (group, role) pair — round 6 default, confirmed with the architect: a group with
// more than one qualifying role gets one row per pair, not a two-step group-then-role picker.
export interface QueueGroupOption {
    groupName: string;
    roleName: string;
    roleLabel: string;
}

export type QueueSelection =
    | { mode: "queue"; roleName: string }
    | { mode: "record_person"; source?: FlussValue }
    // PRIM-024 (round 6): a literal sys_users.id, as opposed to record_person's `source` (a
    // record.<col>/var.<name> reference to resolve at runtime). Written to the binding's
    // `assigneeUser` — same field `assignmentFromBinding` used to read record_person from before
    // this, which is exactly the ambiguity this mode's read side has to resolve (see
    // ActivityNodePanels.tsx assignmentFromBinding).
    | { mode: "fixed_user"; userId: string }
    | null;

export const scopeKindFor = (value: FlussValue | undefined): ScopeKind | "" => {
    if (typeof value === "string" && value.startsWith("record.")) return "record";
    if (typeof value === "string" && value.startsWith("var.")) return "variable";
    return value === undefined ? "" : "literal";
};

export const serializeScopeValue = (
    kind: ScopeKind,
    value: FlussValue
): FlussValue => {
    if (kind === "record") return `record.${String(value).replace(/^record\./, "")}`;
    if (kind === "variable") return `var.${String(value).replace(/^var\./, "")}`;
    return value;
};

export const scopeOptionName = (value: FlussValue | undefined): string => {
    if (typeof value !== "string") return "";
    return value.replace(/^(record|var)\./, "");
};

const typeFamily = (type: string): string => {
    if (["INTEGER", "DECIMAL", "FLOAT", "CURRENCY"].includes(type)) return "number";
    if (["DATE", "DATETIME", "TIME"].includes(type)) return "temporal";
    if (type === "BOOLEAN") return "boolean";
    if (["REFERENCE", "UUID"].includes(type)) return "reference";
    return "text";
};

export const areScopeTypesCompatible = (
    destinationType: string,
    sourceType: string
): boolean => {
    if (!destinationType || !sourceType) return true;
    if (destinationType === "TEXT") {
        return ["TEXT", "CHOICE", "TRANSLATABLE_TEXT", "CODE", "INTEGER", "DECIMAL",
            "BOOLEAN", "DATE", "DATETIME", "UUID"].includes(sourceType);
    }
    // F4-U01: a UUID/REFERENCE destination takes a TEXT or CODE source as the record id.
    if (["UUID", "REFERENCE"].includes(destinationType) && ["TEXT", "CODE"].includes(sourceType)) return true;
    const destinationFamily = typeFamily(destinationType);
    const sourceFamily = typeFamily(sourceType);
    if (destinationFamily === sourceFamily) return true;
    return destinationFamily === "text";
};
